import { Card, Spinner } from "react-bootstrap";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, TooltipProps } from "recharts";
import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { STYLE, COLOR } from "../../../../config/constraits";
import { saveBeforeFilter, addFilter } from "../../../../redux/filtersSlice";
import { generateCsvFile } from "../../../../utils/downloadCsvFile";
import ChartShareActions from "../../../../components/ChartShareActions";
import { RootState } from "../../../../redux/store";
import { ClaimServiceInidicatorBarChart } from "../../types";
import { useClaimServiceIndicatorBarChartData } from "../../hooks/useClaimServiceIndicatorBarChartData";
import { AuditActionEnum } from "../../../auditLogs/types";

interface CustomizedAxisTickProps {
    x: number; // Coordenada X de la posición del tick
    y: number; // Coordenada Y de la posición del tick
    labelText: string;
}

interface ClaimsServiceIndicatorBarChartProps {
    setMessage: (msg: string) => void;
}

const CustomizedAxisTick: React.FC<CustomizedAxisTickProps> = ({ x, y, labelText }) => {
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="end" fill="#666" transform="rotate(-35)">
                {labelText}
            </text>
        </g>
    );
};


const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
    const { t } = useTranslation();

    if (active && payload && payload.length) {
        const total =
            (payload.find(p => p.dataKey === "countOpen")?.value || 0) +
            (payload.find(p => p.dataKey === "countProgress")?.value || 0) +
            (payload.find(p => p.dataKey === "countClosed")?.value || 0);

        return (
            <div style={{ backgroundColor: "#fff", border: "1px solid #ccc", padding: "10px" }}>
                <p><strong>{t(label)}</strong>: {total} </p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color, margin: 0 }}>
                        {entry.name}: {entry.value}
                    </p>
                ))}
                <hr />
            </div>
        );
    }

    return null;
};

export default function ClaimsServiceIndicatorBarChart({ setMessage }: ClaimsServiceIndicatorBarChartProps) {
    const { t } = useTranslation('claim');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { data, isLoading, error } = useClaimServiceIndicatorBarChartData();
    const feelingchartRef = useRef<HTMLDivElement>(null);
    const selectedFilters = useSelector((state: RootState) => state.filters.claims.selectedFilters);

    useEffect(() => {
        if (error) {
            const messageError = (error as any)?.response?.data?.message || (error as Error).message;
            setMessage(messageError);
        }
    }, [error, setMessage]);

    const handleBarClick = (e: string) => {
        const filter = {
            name: 'ServiceIndicator',
            values: [e]
        };
        dispatch(saveBeforeFilter('claims'));
        dispatch(addFilter({ table: 'claims', filter: filter }));
        navigate('/claims/historic');
    };

    const exportBarChartCsv = async (): Promise<void> => {
        const rows = (data ?? []) as ClaimServiceInidicatorBarChart[];
        generateCsvFile({
            headers: [
                t("claim:serviceIndicatorBarChart.export.headerService"),
                t("claim:serviceIndicatorBarChart.export.headerTotalClaims"),
                t("claim:serviceIndicatorBarChart.labelOpen"),
                t("claim:serviceIndicatorBarChart.labelInProgress"),
                t("claim:serviceIndicatorBarChart.labelClosed"),
            ],
            rows,
            mapRow: (item) => {
                const totalClaims = (item.countOpen ?? 0) + (item.countProgress ?? 0) + (item.countClosed ?? 0);
                return [
                    item.serviceDescription ?? "",
                    totalClaims,
                    item.countOpen ?? 0,
                    item.countProgress ?? 0,
                    item.countClosed ?? 0,
                ];
            },
            fileBaseName: "claims_service_indicators",
            trailingSemicolon: true,
        });
    };

    return (
    <>
        {isLoading && (
            <div className="overlay-spinner">
                <Spinner animation="border" variant="light" />
            </div>
        )}
        <div ref={feelingchartRef}>
            <Card
                style={{
                    backgroundColor: "#F5F5F5",
                    border: "none",
                    borderRadius: STYLE.BORDER_RADIUS,
                    height: "400px"
                }}
                className="p-3 d-flex justify-content-center align-items-center text-center"
            >
                <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                    <h4>{t("claim:serviceIndicatorBarChart.labelTitle")}</h4>
                    <div className="d-flex align-items-center">
                        <ChartShareActions
                            chartRef={feelingchartRef}
                            title={t("claim:serviceIndicatorBarChart.labelTitle")}
                            filters={selectedFilters}
                            t={t}
                            exportCsvAction={exportBarChartCsv}
                            exportCsvAudit={{
                                entity: "DashboardIndicator",
                                action: AuditActionEnum.ExportCsv,
                                source: window.location.pathname,
                                description: "CSV export of claim status bar chart",
                                filters: selectedFilters
                            }}
                        />
                    </div>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        width={500}
                        height={300}
                        data={data}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 70,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="serviceDescription" angle={60} height={120} tick={({ x, y, payload }) => <CustomizedAxisTick x={x} y={y} labelText={t(payload.value)} />} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend verticalAlign="bottom" align="center" height={36} />
                        <Bar name={t("claim:serviceIndicatorBarChart.labelOpen")} dataKey="countOpen" onClick={(barData) => handleBarClick(barData.serviceId)}  stackId="a" fill={COLOR.PURPLE} />
                        <Bar name={t("claim:serviceIndicatorBarChart.labelInProgress")} dataKey="countProgress" onClick={(barData) => handleBarClick(barData.serviceId)}  stackId="a" fill={COLOR.TARONJA} />
                        <Bar name={t("claim:serviceIndicatorBarChart.labelClosed")} dataKey="countClosed" onClick={(barData) => handleBarClick(barData.serviceId)}  stackId="a" fill={COLOR.VERD} />
                    </BarChart>
                </ResponsiveContainer>
            </Card>
        </div>
    </>
    );
}
